/**
 * Factoria y configuracion de almacenamiento IndexedDB para FinVoz
 * (spec/02-architecture.md §3, spec/03-data-model.md §8).
 */

import { IndexedDbLocalStore, openWithMigrations, type LocalStore } from "./local-store.js";
import { type Migration, CURRENT_SCHEMA_VERSION } from "./schema.js";
import type { Account } from "../core/account.js";
import { PROFILES_COLLECTION, PROFILE_REGISTRY_DB_NAME, profileDataDbName } from "../profile/profile.js";

export const FINVOZ_COLLECTIONS = [
  "entries",
  "accounts",
  "budgets_goals",
  "quotes",
  // `quote-cache.ts` persiste en "price_quotes" (no en "quotes"). Sin declarar
  // este nombre aquí, IndexedDbLocalStore nunca crea ese object store y toda
  // lectura/escritura de cotizaciones falla en un navegador real (bug latente,
  // no cubierto por los tests previos porque solo ejercían InMemoryLocalStore,
  // que crea colecciones bajo demanda). Corregido junto con la exportación de
  // "inversiones" en la copia de seguridad portátil.
  "price_quotes",
  "outbox",
  "integrity_manifests",
  "metadata_ops",
] as const;

export type FinVozCollection = (typeof FINVOZ_COLLECTIONS)[number];

export const FINVOZ_MIGRATIONS: readonly Migration[] = [
  { fromVersion: 0, toVersion: 1, migrate: (d) => d },
];

export const DEFAULT_ACCOUNTS: readonly Account[] = [
  // Activos (Assets)
  { accountId: "acc-bank", name: "Cuenta Bancaria", type: "asset", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-cash", name: "Efectivo", type: "asset", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-invest", name: "Cartera Inversión", type: "asset", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  // Pasivos (Liabilities)
  { accountId: "acc-card", name: "Tarjeta de Crédito", type: "liability", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-loan", name: "Préstamo Personal", type: "liability", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  // Patrimonio (Equity)
  { accountId: "acc-equity-initial", name: "Patrimonio Inicial", type: "equity", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  // Ingresos (Income)
  { accountId: "acc-salary", name: "Nómina", type: "income", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-returns", name: "Rendimientos e Inversión", type: "income", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-other-income", name: "Otros Ingresos", type: "income", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  // Gastos (Expenses)
  { accountId: "acc-housing", name: "Vivienda y Alquiler", type: "expense", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-groceries", name: "Alimentación y Supermercado", type: "expense", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-transport", name: "Transporte", type: "expense", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-dining", name: "Restauración y Ocio", type: "expense", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-utilities", name: "Suministros (Luz, Agua, Internet)", type: "expense", currency: "PEN", parentId: null, archived: false, instrumentId: null },
  { accountId: "acc-other-expense", name: "Otros Gastos", type: "expense", currency: "PEN", parentId: null, archived: false, instrumentId: null },
];

/**
 * Inicializa el almacen IndexedDB canónico de FinVoz con migraciones hacia adelante.
 * Con perfiles locales (revisión de ADR OQ-1, 2026-09-10), cada perfil tiene su
 * propia base de datos aislada — pasar `profileDataDbName(profileId)` como `dbName`.
 */
export async function createFinVozLocalStore(dbName = "finvoz-db"): Promise<IndexedDbLocalStore> {
  const store = new IndexedDbLocalStore(dbName, FINVOZ_COLLECTIONS);
  await openWithMigrations(store, FINVOZ_MIGRATIONS, async () => {});
  return store;
}

/**
 * Abre el almacén de datos del perfil dado (aislado del resto de perfiles).
 * Envoltorio fino sobre `createFinVozLocalStore` con el nombre de base de
 * datos derivado del `profileId` (FR-PROFILE-2).
 */
export async function createProfileDataStore(profileId: string): Promise<IndexedDbLocalStore> {
  return createFinVozLocalStore(profileDataDbName(profileId));
}

/** Abre el registro de perfiles (separado de los datos de cada perfil). */
export async function createProfileRegistryStore(): Promise<IndexedDbLocalStore> {
  const store = new IndexedDbLocalStore(PROFILE_REGISTRY_DB_NAME, [PROFILES_COLLECTION]);
  await openWithMigrations(store, FINVOZ_MIGRATIONS, async () => {});
  return store;
}

/**
 * Si el plan de cuentas esta vacio en el primer arranque, inserta el plan inicial ofrecido (FR-ACCT-6).
 */
export async function seedInitialAccountsIfEmpty(store: LocalStore): Promise<boolean> {
  const existing = await store.list<Account>("accounts");
  if (existing.length === 0) {
    await store.putBatch(
      "accounts",
      DEFAULT_ACCOUNTS.map((acc) => ({ id: acc.accountId, value: acc })),
    );
    return true;
  }
  return false;
}
